import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  Link as LinkIcon,
  Business as BusinessIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
// import { toast } from 'react-toastify';
import apiClient from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// Tipuri TypeScript
interface PACS {
  id: string;
  name: string;
  base_url: string;
  auth_user?: string;
  is_default: boolean;
  is_external?: boolean;
  verify_ssl?: boolean;
  institution_id?: string;
  tenant_id: string;
  created_at: string;
  tenant_name?: string;
  institutions_count?: number;
}

interface PACSAdmin extends PACS {
  auth_password?: string;
  institution_id?: string;
}

interface PACSFormData {
  name: string;
  base_url: string;
  auth_user: string;
  auth_password: string;
  is_default: boolean;
  is_external: boolean;
  verify_ssl: boolean;
  institution_id: string;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
}

interface Institution {
  id: string;
  name: string;
  pacs_id?: string;
  tenant_id: string;
}

const AdminPacsPage: React.FC = () => {
  const { user } = useAuth();
  const [pacsList, setPacsList] = useState<PACS[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedPacs, setSelectedPacs] = useState<PACSAdmin | null>(null);
  const [formData, setFormData] = useState<PACSFormData>({
    name: '',
    base_url: '',
    auth_user: '', // Nu mai predefinim credențialele
    auth_password: '', // Utilizatorul trebuie să le introducă manual
    is_default: false,
    is_external: false,
    verify_ssl: true,
    institution_id: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [dialogLoading, setDialogLoading] = useState(false);

  // Încarcă datele
  const loadData = async () => {
    try {
      setLoading(true);
      
      // Încarcă PACS-urile cu detalii admin
      const pacsResponse = await apiClient.get<PACSAdmin[]>('/tenants/pacs');
      console.log('PACS Response:', pacsResponse.data);
      
      if (pacsResponse.data && Array.isArray(pacsResponse.data)) {
        const pacsWithDetails = await Promise.all(
          pacsResponse.data.map(async (pacs: PACS) => {
            try {
              // Încarcă detalii admin pentru fiecare PACS
              const adminResponse = await apiClient.get<PACSAdmin>(`/pacs/admin/${pacs.id}`);
              return adminResponse.data;
            } catch (error) {
              console.error(`Eroare la încărcarea detaliilor admin pentru PACS ${pacs.id}:`, error);
              return pacs;
            }
          })
        );
        setPacsList(pacsWithDetails);
      } else {
        console.error('PACS response is not an array:', pacsResponse.data);
        setPacsList([]);
      }

      // Încarcă tenant-urile
      const tenantsResponse = await apiClient.get<Tenant[]>('/tenants/');
      console.log('Tenants Response:', tenantsResponse.data);
      if (tenantsResponse.data && Array.isArray(tenantsResponse.data)) {
        setTenants(tenantsResponse.data);
      } else {
        console.error('Tenants response is invalid:', tenantsResponse.data);
        setTenants([]);
      }

      // Încarcă instituțiile
      const institutionsResponse = await apiClient.get<Institution[]>('/tenants/institutions');
      console.log('Institutions Response:', institutionsResponse.data);
      if (institutionsResponse.data && Array.isArray(institutionsResponse.data)) {
        setInstitutions(institutionsResponse.data);
      } else {
        console.error('Institutions response is invalid:', institutionsResponse.data);
        setInstitutions([]);
      }

    } catch (error: any) {
      console.error('Eroare la încărcarea datelor:', error);
      console.error('Eroare la încărcarea datelor PACS');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filtrare PACS-uri
  const filteredPacs = (pacsList || []).filter(pacs =>
    pacs && pacs.name && (
      pacs.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pacs.base_url.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pacs.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Helper functions
  const getInstitutionName = (institutionId: string): string => {
    const institution = institutions.find(i => i.id === institutionId);
    return institution ? institution.name : 'Necunoscută';
  };

  // Validare formular
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Numele PACS este obligatoriu';
    }

    if (!formData.base_url.trim()) {
      errors.base_url = 'URL-ul PACS este obligatoriu';
    }

    if (!formData.institution_id) {
      errors.institution_id = 'Instituția este obligatorie';
    }

    // Info: credențialele sunt opționale, backend-ul va pune default (proxy + parola din orthanc.json)

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Creare PACS nou
  const handleCreatePacs = async () => {
    if (!validateForm()) return;

    try {
      setDialogLoading(true);
      
      const createData = {
        name: formData.name,
        base_url: formData.base_url,
        auth_user: formData.auth_user, // Backend-ul va pune default dacă e gol
        auth_password: formData.auth_password, // Backend-ul va pune default dacă e gol
        is_default: formData.is_default,
        is_external: formData.is_external,
        verify_ssl: formData.verify_ssl,
        institution_id: formData.institution_id
      };

      await apiClient.post('/tenants/pacs', createData);
      
      console.log('PACS creat cu succes');
      setShowCreateDialog(false);
      resetForm();
      loadData();
    } catch (error: any) {
      console.error('Eroare la crearea PACS:', error);
      console.error(error.response?.data?.detail || 'Eroare la crearea PACS');
    } finally {
      setDialogLoading(false);
    }
  };

  // Editare PACS
  const handleEditPacs = async () => {
    if (!validateForm() || !selectedPacs) return;

    try {
      setDialogLoading(true);
      
      const updateData = {
        name: formData.name,
        base_url: formData.base_url,
        auth_user: formData.auth_user, // Backend-ul gestionează defaults
        auth_password: formData.auth_password, // Backend-ul gestionează defaults
        is_default: formData.is_default,
        is_external: formData.is_external,
        verify_ssl: formData.verify_ssl,
        institution_id: formData.institution_id
      };

      await apiClient.put(`/tenants/pacs/${selectedPacs.id}`, updateData);
      
      console.log('PACS actualizat cu succes');
      setShowEditDialog(false);
      resetForm();
      loadData();
    } catch (error: any) {
      console.error('Eroare la actualizarea PACS:', error);
      console.error(error.response?.data?.detail || 'Eroare la actualizarea PACS');
    } finally {
      setDialogLoading(false);
    }
  };

  // Ștergere PACS
  const handleDeletePacs = async (pacs: PACS) => {
    const confirmMessage = `Ești sigur că vrei să ștergi PACS-ul "${pacs.name}"?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await apiClient.delete(`/tenants/pacs/${pacs.id}`);
      console.log('PACS șters cu succes:', response.data);
      
      // Afișează detalii despre cleanup dacă sunt disponibile
      if (response.data.cleanup_details && response.data.cleanup_details.length > 0) {
        const cleanupMessage = response.data.cleanup_details.join('\n• ');
        alert(`PACS șters cu succes!\n\nCleanup realizat:\n• ${cleanupMessage}`);
      } else {
        alert('PACS șters cu succes!');
      }
      
      loadData();
    } catch (error: any) {
      console.error('Eroare la ștergerea PACS:', error);
      const errorMessage = error.response?.data?.detail || 'Eroare la ștergerea PACS';
      alert(`Eroare la ștergerea PACS: ${errorMessage}`);
    }
  };

  // Resetare formular
  const resetForm = () => {
    setFormData({
      name: '',
      base_url: '',
      auth_user: '', // Câmpuri goale pentru credențiale
      auth_password: '', // Utilizatorul le introduce manual
      is_default: false,
      is_external: false,
      verify_ssl: true,
      institution_id: '',
      // Containerization removed
    });
    setFormErrors({});
    setSelectedPacs(null);
  };

  // Deschidere dialog creare
  const handleOpenCreateDialog = () => {
    resetForm();
    
    // Automatically set the first institution for new PACS
    if (institutions.length > 0) {
      setFormData(prev => ({ ...prev, institution_id: institutions[0].id }));
    }
    
    setShowCreateDialog(true);
  };

  // Deschidere dialog editare
  const handleOpenEditDialog = async (pacs: PACS) => {
    try {
      // Încarcă detalii admin pentru editare
      const adminResponse = await apiClient.get<PACSAdmin>(`/pacs/admin/${pacs.id}`);
      const pacsAdmin = adminResponse.data;
      
      setSelectedPacs(pacsAdmin);
      setFormData({
        name: pacsAdmin.name,
        base_url: pacsAdmin.base_url,
        auth_user: pacsAdmin.auth_user || '',
        auth_password: pacsAdmin.auth_password || '',
        is_default: pacsAdmin.is_default,
        is_external: pacsAdmin.is_external ?? false,
        verify_ssl: pacsAdmin.verify_ssl ?? true,
        institution_id: pacsAdmin.institution_id || '',
        // Containerization fields removed
      });
      setFormErrors({});
      setShowEditDialog(true);
    } catch (error: any) {
      console.error('Eroare la încărcarea detaliilor PACS:', error);
      console.error('Eroare la încărcarea detaliilor PACS');
    }
  };

  // Deschidere dialog vizualizare
  const handleOpenViewDialog = async (pacs: PACS) => {
    try {
      // Încarcă detalii admin pentru vizualizare
      const adminResponse = await apiClient.get<PACSAdmin>(`/pacs/admin/${pacs.id}`);
      setSelectedPacs(adminResponse.data);
      setShowViewDialog(true);
    } catch (error: any) {
      console.error('Eroare la încărcarea detaliilor PACS:', error);
      console.error('Eroare la încărcarea detaliilor PACS');
    }
  };

  // Obține numele tenant-ului
  const getTenantName = (tenantId: string) => {
    const tenant = (tenants || []).find(t => t && t.id === tenantId);
    return tenant?.name || 'Necunoscut';
  };

  // Obține instituția în care este PACS-ul  
  const getPacsInstitution = (pacs: PACS) => {
    // Găsește instituția după PACS.institution_id (nu după inst.pacs_id)
    if (!pacs.institution_id) return null;
    return (institutions || []).find(inst => inst && inst.id === pacs.institution_id);
  };

  // Reîncarcă configurația Nginx
  const handleReloadNginx = async () => {
    try {
      setDialogLoading(true);
      
      await apiClient.post('/nginx-config/reload');
      console.log('Configurația Nginx a fost actualizată cu succes');
      
    } catch (error: any) {
      console.error('Eroare la actualizarea configurației Nginx:', error);
      console.error(error.response?.data?.detail || 'Eroare la actualizarea configurației Nginx');
    } finally {
      setDialogLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Compact styles
  const inputSx = { '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } };
  const dialogPaperSx = {
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    '& .MuiInputLabel-root': { color: 'text.secondary', fontSize: '0.85rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
    '& .MuiOutlinedInput-root': {
      fontSize: '0.875rem',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
    },
    '& .MuiSelect-select': { color: 'text.primary' },
    '& .MuiMenuItem-root': { fontSize: '0.875rem' },
  };
  const tableSx = { '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.78rem' }, '& .MuiTableBody-root .MuiIconButton-root': { p: 0.25 }, '& .MuiTableBody-root .MuiSvgIcon-root': { fontSize: '1rem' } };

  return (
    <Box p={1.5}>
      {/* Header - Compact */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1.5, gap: 1 }}>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>Actualizează</Button>
        <Button variant="outlined" startIcon={<SettingsIcon />} onClick={handleReloadNginx} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>Nginx</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateDialog} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>Adaugă PACS</Button>
      </Box>

      {/* Stats - Compact cu etichete vizibile */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
        {[
          { icon: <StorageIcon sx={{ fontSize: 20, color: 'primary.main' }} />, val: (pacsList || []).length, label: 'Total PACS' },
          { icon: <BusinessIcon sx={{ fontSize: 20, color: 'success.main' }} />, val: (tenants || []).length, label: 'Tenant-uri' },
          { icon: <LinkIcon sx={{ fontSize: 20, color: 'info.main' }} />, val: (institutions || []).length, label: 'Instituții' },
          { icon: <SettingsIcon sx={{ fontSize: 20, color: 'warning.main' }} />, val: (pacsList || []).filter(p => p && p.is_default).length, label: 'Default' },
        ].map((s, i) => (
          <Paper key={i} sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 130 }}>
            {s.icon}
            <Box>
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, lineHeight: 1 }}>{s.val}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.25 }}>{s.label}</Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Search - Compact */}
      <Paper sx={{ p: 1, mb: 1.5 }}>
        <TextField fullWidth placeholder="Caută PACS după nume, URL sau tenant..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} size="small" sx={inputSx} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }} />
      </Paper>

      {/* Table - Compact */}
      <TableContainer component={Paper} sx={tableSx}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.900', '& th': { color: 'common.white', fontSize: '0.75rem', fontWeight: 600, py: 0.5, px: 1 } }}>
              <TableCell>Nume</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Tip</TableCell>
              <TableCell>Hostname</TableCell>
              <TableCell>Tenant</TableCell>
              <TableCell>Instituție</TableCell>
              <TableCell>OHIF</TableCell>
              <TableCell align="center">Acțiuni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPacs.map((pacs) => {
              const pacsInstitution = getPacsInstitution(pacs);
              return (
                <TableRow key={pacs.id} hover>
                  <TableCell><Typography sx={{ fontSize: '0.78rem', fontWeight: 500 }}>{pacs.name}</Typography></TableCell>
                  <TableCell><Chip label={pacs.is_default ? 'Default' : 'Sec'} size="small" color={pacs.is_default ? 'success' : 'default'} sx={{ height: 18, fontSize: '0.68rem' }} /></TableCell>
                  <TableCell><Chip label={pacs.is_external ? 'Ext' : 'Int'} size="small" color={pacs.is_external ? 'warning' : 'primary'} variant={pacs.is_external ? 'outlined' : 'filled'} sx={{ height: 18, fontSize: '0.68rem' }} /></TableCell>
                  <TableCell><Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace' }}>{new URL(pacs.base_url).hostname}</Typography></TableCell>
                  <TableCell><Chip label={getTenantName(pacs.tenant_id)} size="small" color="secondary" sx={{ height: 18, fontSize: '0.68rem' }} /></TableCell>
                  <TableCell>{pacsInstitution ? <Chip label={pacsInstitution.name} size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.68rem' }} /> : <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>-</Typography>}</TableCell>
                  <TableCell><Button size="small" variant="outlined" href="/ris-pacs/ohif/viewer" target="_blank" sx={{ height: 22, fontSize: '0.68rem', textTransform: 'none' }}>OHIF</Button></TableCell>
                  <TableCell align="center">
                    <Tooltip title="Vezi"><IconButton size="small" onClick={() => handleOpenViewDialog(pacs)} color="info"><ViewIcon /></IconButton></Tooltip>
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpenEditDialog(pacs)} color="primary"><EditIcon /></IconButton></Tooltip>
                    {user?.role === 'admin' && <Tooltip title="Șterge"><IconButton size="small" onClick={() => handleDeletePacs(pacs)} color="error"><DeleteIcon /></IconButton></Tooltip>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog Creare PACS - Compact & Consistent */}
      <Dialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}>Adaugă PACS Nou</DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField fullWidth label="Nume PACS *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} error={!!formErrors.name} helperText={formErrors.name} size="small" sx={inputSx} />
              <FormControl fullWidth size="small" required error={!!formErrors.institution_id} sx={inputSx}>
                <InputLabel>Instituție *</InputLabel>
                <Select value={formData.institution_id} onChange={(e) => setFormData({ ...formData, institution_id: e.target.value })} label="Instituție *">
                  {(institutions || []).map((inst) => <MenuItem key={inst.id} value={inst.id} sx={{ fontSize: '0.8rem' }}>{inst.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <Divider sx={{ my: 0.5 }}><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Configurație PACS</Typography></Divider>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField fullWidth label="Base URL *" value={formData.base_url} onChange={(e) => setFormData({ ...formData, base_url: e.target.value })} error={!!formErrors.base_url} helperText={formErrors.base_url || 'Ex: http://pacs.spital.ro:8042'} size="small" sx={inputSx} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField fullWidth label="Utilizator Auth" value={formData.auth_user} onChange={(e) => setFormData({ ...formData, auth_user: e.target.value })} placeholder="Opțional" helperText="Default: 'proxy'" size="small" sx={inputSx} />
              <TextField fullWidth label="Parolă Auth" type="password" value={formData.auth_password} onChange={(e) => setFormData({ ...formData, auth_password: e.target.value })} placeholder="Opțional" helperText="Default: din orthanc.json" size="small" sx={inputSx} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
              <FormControlLabel control={<Switch checked={formData.is_default} onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })} size="small" />} label={<Typography sx={{ fontSize: '0.78rem' }}>PACS Default</Typography>} />
              <FormControlLabel control={<Switch checked={formData.is_external} onChange={(e) => setFormData({ ...formData, is_external: e.target.checked })} size="small" />} label={<Typography sx={{ fontSize: '0.78rem' }}>PACS Extern</Typography>} />
              <FormControlLabel control={<Switch checked={formData.verify_ssl} onChange={(e) => setFormData({ ...formData, verify_ssl: e.target.checked })} size="small" />} label={<Typography sx={{ fontSize: '0.78rem' }}>Verificare SSL</Typography>} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setShowCreateDialog(false)} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>Anulează</Button>
          <Button onClick={handleCreatePacs} variant="contained" disabled={dialogLoading} size="small" startIcon={dialogLoading ? <CircularProgress size={14} /> : <AddIcon sx={{ fontSize: 16 }} />} sx={{ fontSize: '0.78rem', textTransform: 'none' }}>{dialogLoading ? 'Se creează...' : 'Creează PACS'}</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Editare PACS - Compact & Consistent */}
      <Dialog open={showEditDialog} onClose={() => setShowEditDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}>Editează PACS</DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField fullWidth label="Nume PACS *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} error={!!formErrors.name} helperText={formErrors.name} size="small" sx={inputSx} />
              <TextField fullWidth label="Instituție" value={getInstitutionName(formData.institution_id)} disabled size="small" sx={inputSx} helperText="Nu se poate modifica" />
            </Box>
            <TextField fullWidth label="URL PACS *" value={formData.base_url} onChange={(e) => setFormData({ ...formData, base_url: e.target.value })} error={!!formErrors.base_url} helperText={formErrors.base_url || 'Ex: http://pacs.example.com:8042'} size="small" sx={inputSx} />
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField fullWidth label="Utilizator" value={formData.auth_user} onChange={(e) => setFormData({ ...formData, auth_user: e.target.value })} size="small" sx={inputSx} />
              <TextField fullWidth label="Parolă" type="password" value={formData.auth_password} onChange={(e) => setFormData({ ...formData, auth_password: e.target.value })} helperText="Gol = păstrează existentă" size="small" sx={inputSx} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
              <FormControlLabel control={<Switch checked={formData.is_default} onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })} size="small" />} label={<Typography sx={{ fontSize: '0.78rem' }}>PACS Default</Typography>} />
              <FormControlLabel control={<Switch checked={formData.is_external} onChange={(e) => setFormData({ ...formData, is_external: e.target.checked })} size="small" />} label={<Typography sx={{ fontSize: '0.78rem' }}>PACS Extern</Typography>} />
              <FormControlLabel control={<Switch checked={formData.verify_ssl} onChange={(e) => setFormData({ ...formData, verify_ssl: e.target.checked })} size="small" />} label={<Typography sx={{ fontSize: '0.78rem' }}>Verificare SSL</Typography>} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setShowEditDialog(false)} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>Anulează</Button>
          <Button onClick={handleEditPacs} variant="contained" disabled={dialogLoading} size="small" startIcon={dialogLoading ? <CircularProgress size={14} /> : <EditIcon sx={{ fontSize: 16 }} />} sx={{ fontSize: '0.78rem', textTransform: 'none' }}>{dialogLoading ? 'Se actualizează...' : 'Actualizează'}</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Vizualizare PACS - Compact & Consistent */}
      <Dialog open={showViewDialog} onClose={() => setShowViewDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}>Detalii PACS</DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {selectedPacs && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Nume</Typography><Typography sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{selectedPacs.name}</Typography></Box>
                <Box><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Status</Typography><Chip label={selectedPacs.is_default ? 'Default' : 'Normal'} size="small" color={selectedPacs.is_default ? 'success' : 'default'} sx={{ height: 20, fontSize: '0.7rem' }} /></Box>
              </Box>
              <Box><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>URL</Typography><Typography sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{selectedPacs.base_url}</Typography></Box>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Utilizator</Typography><Typography sx={{ fontSize: '0.85rem' }}>{selectedPacs.auth_user || '-'}</Typography></Box>
                <Box><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Parolă</Typography><Typography sx={{ fontSize: '0.85rem' }}>{selectedPacs.auth_password ? '••••••••' : '-'}</Typography></Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Tenant</Typography><Typography sx={{ fontSize: '0.85rem' }}>{getTenantName(selectedPacs.tenant_id)}</Typography></Box>
                <Box><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Creat</Typography><Typography sx={{ fontSize: '0.85rem' }}>{new Date(selectedPacs.created_at).toLocaleString('ro-RO')}</Typography></Box>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              <Box><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>Instituții Asociate</Typography>
                {(() => { const inst = getPacsInstitution(selectedPacs); return inst ? <Chip label={inst.name} size="small" color="info" variant="outlined" sx={{ height: 22, fontSize: '0.72rem' }} /> : <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>-</Typography>; })()}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setShowViewDialog(false)} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>Închide</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPacsPage;
